import React, { useEffect, useState, useContext } from "react";
import { useNavigate } from "react-router-dom";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import Axios from "axios";
import DOMPurify from "dompurify";
import { buildUserState } from "../../utilities/tokenUtils";
import UserContext from "../../context/UserContext";
import toast from 'react-hot-toast';

const schema = yup.object().shape({
    previouspassword: yup
        .string()
        .required("Contraseña actual es requerida"),
    password: yup
        .string()
        .required("Nueva contraseña es requerida")
        .min(14, "Mínimo 14 caracteres")
        .matches(
            /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)/,
            "Debe contener mayúscula, minúscula y número"
        )
        .matches(
            /[!@#$%^&*(),.?":{}|<>\-_+=[\]\\/]/,
            "Debe incluir al menos un carácter especial"
        ),
    passwordCheck: yup
        .string()
        .required("Confirma la nueva contraseña")
        .oneOf([yup.ref("password"), null], "Las contraseñas deben coincidir"),
});

export default function PersonalConfig() {
    const [isSubmitting, setIsSubmitting] = useState(false);

    const {
        register,
        handleSubmit,
        formState: { errors },
    } = useForm({
        resolver: yupResolver(schema),
    });

    const { userData, setUserData } = useContext(UserContext);
    const navigate = useNavigate();

    useEffect(() => {
        if (!userData.token) navigate('/admin/login');
    }, [navigate, userData.token]);

    const onSubmit = async (data) => {
        setIsSubmitting(true);
        try {
            if (userData.token) {
                const sanitizedData = {
                    previouspassword: DOMPurify.sanitize(data.previouspassword),
                    password: DOMPurify.sanitize(data.password),
                    passwordCheck: DOMPurify.sanitize(data.passwordCheck),
                };

                const res = await Axios.put(
                    `${process.env.REACT_APP_BACK_DOMAIN}/users/update-password`,
                    sanitizedData,
                    {
                        headers: {
                            "x-auth-token": userData.token,
                        },
                    }
                );

                if (res.data.token) {
                    localStorage.setItem("auth-token", res.data.token);
                    setUserData(buildUserState(res.data.token));
                }

                toast.success("Contraseña actualizada correctamente");
                navigate('/');
            }
        } catch (err) {
            toast.error(err.response?.data?.msg || "Error al actualizar la contraseña");
        } finally {
            setIsSubmitting(false);
        }
    };


    return (
        <div className="page">
            {userData.passwordExpired && (
                <div style={{ background: "#fff2f2", border: "1px solid #e00000", borderRadius: "6px", padding: "12px 16px", marginBottom: "16px", color: "#b90000" }}>
                    <strong>Su contraseña ha expirado.</strong> Debe actualizarla para poder continuar usando la aplicación.
                </div>
            )}
            {userData.passwordExpiringSoon && !userData.passwordExpired && (
                <div style={{ background: "#fff8e7", border: "1px solid #ffb800", borderRadius: "6px", padding: "12px 16px", marginBottom: "16px", color: "#a07000" }}>
                    <strong>Su contraseña expirará pronto.</strong> Le recomendamos actualizarla antes de que se bloquee el acceso.
                </div>
            )}
            <div style={{ display: "flex", justifyContent: "space-between" }}>
                {!userData.passwordExpired && (
                    <button className="Button" onClick={() => navigate('/')}>Regresar</button>
                )}
                <button className="Button Button_primary" onClick={() => navigate('/admin/mfa-setup')}>Configurar MFA</button>
            </div>
            <div className="container">
                <h2>Actualizar Contraseña</h2>
                <div className="info-block" style={{ marginBottom: "12px" }}>
                    <strong>Requisitos de contraseña:</strong>
                    <ul style={{ margin: "4px 0 0 16px" }}>
                        <li>Mínimo 14 caracteres</li>
                        <li>Al menos una letra mayúscula</li>
                        <li>Al menos una letra minúscula</li>
                        <li>Al menos un número</li>
                        <li>Al menos un carácter especial (!@#$%^&amp;*…)</li>
                        <li>No puede coincidir con las últimas 5 contraseñas</li>
                        <li>Debe cambiarse cada 90 días</li>
                    </ul>
                </div>
                <form className="Form" onSubmit={handleSubmit(onSubmit)}>
                    <div className="Form-Field">
                        <label className="Form-Label" htmlFor="prevpwd">Contraseña Actual:</label>
                        <input
                            className="Form-Input"
                            type="password"
                            id="previouspassword"
                            {...register("previouspassword")} />
                        {errors.previouspassword && (
                            <span style={{ color: "red" }}>
                                {errors.previouspassword.message}
                            </span>
                        )}
                    </div>
                    <div className="Form-Field">
                        <label className="Form-Label" htmlFor="pwd">Nueva Contraseña:</label>
                        <input
                            className="Form-Input"
                            type="password"
                            id="password"
                            placeholder="Mín. 14 chars, mayúscula, minúscula, número y carácter especial"
                            {...register("password")} />
                        {errors.password && (
                            <span style={{ color: "red" }}>
                                {errors.password.message}
                            </span>
                        )}
                    </div>
                    <div className="Form-Field">
                        <label className="Form-Label" htmlFor="check_pwd">Confirmar Contraseña:</label>
                        <input
                            className="Form-Input"
                            type="password"
                            id="passwordCheck"
                            {...register("passwordCheck")} />
                        {errors.passwordCheck && (
                            <span style={{ color: "red" }}>
                                {errors.passwordCheck.message}
                            </span>
                        )}
                    </div>
                    <input className="Button Button_primary" type="submit" value={isSubmitting ? "Actualizando..." : "Actualizar"} disabled={isSubmitting} />
                </form>
            </div>
        </div>
    );
}
